import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Label, Button, Tooltip, ProgressBar } from 'mw-style-react';
import AppUtils from '@control-front-end/utils/utils';
import { RequestStatus } from 'constants';
import { CLEAR_SCRIPT_STRUCTURE } from '@control-front-end/common/constants/scripts';
import { useIntl, useModal } from '@control-front-end/app/src/hooks';
import useScriptContentActions from '@control-front-end/scripts/hooks/useScriptContentActions';
import useActorsBagActions from '@control-front-end/app/src/routes/ActorsGraph/hooks/useActorsBagActions';
import ScriptHeader from './components/ScriptHeader';
import ScriptFileEditor from './components/ScriptFileEditor';
import ScriptFilePreview from './components/ScriptFilePreview';
import StructureItem from './components/StructureItem';
import mes from './intl';
import './ScriptEditor.scss';

function ScriptEditor({ id, history }) {
  const t = useIntl();
  const dispatch = useDispatch();
  const { content, reqStatus } = useSelector((state) => state.scriptContent);
  const { active: accId } = useSelector((state) => state.accounts);
  const [activeScript, setActiveScript] = useState();
  const [showPanel, togglePanel] = useState(true);
  const [activeFile, setActiveFile] = useState(null);
  const [activeEnv, setActiveEnv] = useState(null);
  const [changedAt, setChangedAt] = useState();
  const [smartFormGraphId, setSmartFormGraphId] = useState(null);
  const {
    handleGetScriptActor,
    handleGetScriptEnvs,
    handleGetStructure,
    handleCreatePage,
    handleCreateObject,
    handleChangeFile,
    handleRemoveFile,
    handleShareScript,
    handleRemoveScript,
    handleDeploy,
    handleDuplicate,
  } = useScriptContentActions({
    accId,
    script: activeScript,
    content,
    setActiveFile,
    setActiveScript,
  });
  const { handleGetSmartFormGraph } = useActorsBagActions({
    accId,
    activeElement: activeScript,
  });
  const { open: openRemoveConfirmModal, close: closeRemoveConfirmModal } =
    useModal('InfoModal', {});
  const canModify =
    activeScript && activeScript.privs && activeScript.privs.modify;
  const readOnly =
    !canModify || (activeEnv && activeEnv.title === 'production');

  useEffect(() => {
    if (id) {
      handleGetScriptActor({
        id,
        callback: (s) => {
          handleGetScriptEnvs({
            id,
            callback: (data) => {
              s.envs = AppUtils.sort(data, 'title');
              setActiveScript(s);
              setActiveEnv(data[0]);
            },
          });
        },
      });
    }
    return () => dispatch({ type: CLEAR_SCRIPT_STRUCTURE });
  }, [id]);

  useEffect(() => {
    handleGetSmartFormGraph((data) => {
      const graphId = data?.[0]?.actorId;
      setSmartFormGraphId(graphId || null);
    });
  }, [activeScript?.id]);

  const handleOpenSmartFormGraph = () => {
    if (!smartFormGraphId) return;
    const url = AppUtils.makeUrl(
      `/actors_graph/${accId}/graph/${smartFormGraphId}/layers/0`
    );
    window.open(url);
  };

  /**
   * Close file if it was removed or removed parent folder
   */
  const closeRemovedFile = (obj) => {
    if (!activeFile) return;
    const activeFileRemoved = activeFile.id === obj.id;
    const activeFileParentRemoved =
      obj.children && obj.children.find((item) => item.id === activeFile.id);
    if (activeFileRemoved || activeFileParentRemoved) setActiveFile(null);
  };

  /**
   * Remove or folder with confirmation
   */
  const handleRemove = (obj) =>
    openRemoveConfirmModal({
      title: 'removeFile',
      text: {
        text: 'removeFileConfirm',
        values: { type: obj.objType, title: obj.title },
      },
      buttons: [
        {
          type: 'error',
          label: t(mes.remove),
          onClick: () => {
            handleRemoveFile(obj);
            closeRemovedFile(obj);
            closeRemoveConfirmModal();
          },
        },
      ],
    });

  /**
   * Open index page or first page
   */
  const openInitialPageFile = (root) => {
    const pagesFolder = root.children.find((i) => i.title === 'pages');
    if (pagesFolder.children.length) {
      let firstPage = pagesFolder.children.find((i) => i.title === 'index');
      if (!firstPage) {
        [firstPage] = pagesFolder.children;
      }
      const pageConfig = firstPage.children.find(
        (i) => i.title.indexOf('config') !== -1
      );
      setActiveFile(pageConfig);
    }
  };

  useEffect(() => {
    if (!activeScript || !activeEnv) return;
    const canViewStructure = activeScript.privs && activeScript.privs.view;
    if (canViewStructure) {
      handleGetStructure({
        envId: activeEnv.id,
        callback: openInitialPageFile,
      });
    } else {
      history.push('/access_denied');
    }
  }, [activeEnv]);

  /**
   * Edit structure buttons
   */
  const renderSystemBtns = (folder) => {
    const { id: folderId, title } = folder;
    if (title === 'pages') {
      return (
        <Tooltip value={t(mes.newPage)}>
          <Button
            styleName="se__content__structure__item__actions__btn"
            type="tertiary"
            icon="folder_add"
            onClick={handleCreatePage}
          />
        </Tooltip>
      );
    }
    return (
      <>
        <Tooltip value={t(mes.newFolder)}>
          <Button
            styleName="se__content__structure__item__actions__btn"
            type="tertiary"
            icon="folder_add"
            onClick={() => handleCreateObject({ folderId, objType: 'folder' })}
          />
        </Tooltip>
        <Tooltip value={t(mes.newFile)}>
          <Button
            styleName="se__content__structure__item__actions__btn"
            type="tertiary"
            icon="file_add"
            onClick={() => handleCreateObject({ folderId, objType: 'file' })}
          />
        </Tooltip>
      </>
    );
  };

  const recursiveRenderContent = (
    objContent,
    level,
    isAllowedCreating = false
  ) => {
    if (!objContent) return null;
    const {
      id: objId,
      objType,
      isSystem,
      isStatic,
      isNew,
      title,
      icon,
      children,
    } = objContent;
    const isFolder = objType === 'folder';
    const allowActions =
      isFolder && (isAllowedCreating || title === 'definitions');

    return (
      <Fragment key={objId}>
        <StructureItem
          level={level}
          id={objId}
          objType={objType}
          title={title}
          icon={icon}
          isNew={isNew}
          isSystem={isSystem}
          isStatic={isStatic}
          activeItem={activeFile}
          readOnly={readOnly}
          customActions={
            allowActions || title === 'pages'
              ? renderSystemBtns({ id: objId, title })
              : null
          }
          handleSelect={() => {
            if (isFolder) return;
            setActiveFile(objContent);
          }}
          handleShowPage={() => {
            if (isFolder && children.length) {
              const pageConfig = children.find(
                (i) => i.title.indexOf('config') !== -1
              );
              setActiveFile(pageConfig);
            }
          }}
          handleRemove={() => handleRemove(objContent)}
          handleUpdate={(newTitle, newIsStatic) => {
            const obj = { ...objContent };
            if (isFolder) {
              delete obj.children;
            }
            obj.title = newTitle;
            obj.isStatic = newIsStatic;
            handleChangeFile({ obj });
          }}
          handleDuplicate={() => handleDuplicate(objContent)}
        >
          {children && children.length > 0
            ? children.map((i) =>
                recursiveRenderContent(i, level + 1, allowActions)
              )
            : null}
        </StructureItem>
      </Fragment>
    );
  };

  const renderLoader = () => (
    <div styleName="se__content__loader">
      <ProgressBar size="large" type="circle" />
    </div>
  );

  const renderStructure = () => {
    if (!content.children) return null;
    return content.children.map((i) => recursiveRenderContent(i, 0));
  };

  const noFileSelected = () => (
    <div styleName="se__empty">
      <Icon type="file" size="xlarge" />
      <Label value={t(mes.selectOrCreateFile)} />
    </div>
  );

  if (!activeScript) return renderLoader();

  return (
    <div styleName="se">
      <ScriptHeader
        accId={accId}
        activeScript={activeScript}
        activeEnv={activeEnv}
        readOnly={readOnly}
        hasGraph={!!smartFormGraphId}
        handleDeploy={handleDeploy}
        handleShareScript={handleShareScript}
        handleRemoveScript={handleRemoveScript}
        handleChangeEnv={({ value }) => {
          setActiveFile(null);
          setActiveEnv(value);
        }}
        handleUpdateScript={(data) =>
          setActiveScript({ ...activeScript, ...data })
        }
        handleOpenScriptGraph={handleOpenSmartFormGraph}
      />
      <div styleName="se__content">
        <div styleName="se__content__structure">
          <div styleName="se__content__header">
            <Label fontWeight="semibold" value={t(mes.files)} />
          </div>
          {reqStatus !== RequestStatus.SUCCESS
            ? renderLoader()
            : renderStructure()}
        </div>
        {activeFile ? (
          <div styleName="se__content__file">
            <ScriptFileEditor
              key={activeFile.id}
              fileId={activeFile.id}
              readOnly={readOnly}
              showPanel={showPanel}
              handleTogglePanel={() => togglePanel(!showPanel)}
              handleChange={(data) => {
                const now = new Date().getTime();
                setChangedAt(now);
                handleChangeFile(data);
              }}
            />
            {showPanel && content.children ? (
              <ScriptFilePreview
                accId={accId}
                fileId={activeFile.id}
                scriptRef={activeScript.ref}
                stage={activeEnv.title}
                pagesFolderId={content.children[0].id}
                changedAt={changedAt}
              />
            ) : null}
          </div>
        ) : (
          noFileSelected()
        )}
      </div>
    </div>
  );
}

ScriptEditor.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ScriptEditor;
